import InternalChatsAPI from '../../api/internalChats';

// Fila de envio por chat (NÃO é state reativo — só ordena as chamadas de
// rede de verdade). Sem isso, mandar várias mensagens rápido dispara POSTs
// concorrentes que podem chegar ao servidor fora de ordem (bug real achado
// em teste: "Rapida 1/2/3/4" persistia embaralhado tipo "3,1,2,4").
const sendQueues = {};

export const initialState = {
  chats: [],
  activeChat: null,
  // messages keyed by chatId: { [chatId]: Message[] }
  messages: {},
  agentsCache: [],
  // unread counts keyed by chatId: { [chatId]: number }
  unreadByChat: {},
  // Onde o usuário parou em cada conversa, capturado na PRIMEIRA chamada de
  // markRead. Vive no store, e não no componente, porque dois lugares chamam
  // markRead (a rota, ao selecionar, e o ChatView, ao montar) e a segunda
  // chamada já devolve "agora" — quem guardasse localmente pegaria o valor
  // errado dependendo da ordem.
  unreadMarkerByChat: {},
  uiFlags: {
    isFetchingChats: false,
    fetchChatsError: false,
    isFetchingMessages: false,
    isSendingMessage: false,
    isCreatingDm: false,
    isCreatingGroup: false,
    isCreatingChannel: false,
  },
};

const state = { ...initialState };

// Guard: a API pode responder 204/503 com corpo vazio ("") → chats viraria
// string e quebraria .filter. Sempre tratamos como array.
const safeChats = $state => (Array.isArray($state.chats) ? $state.chats : []);

const getters = {
  // Onde o usuário parou nesta conversa. `null` = sem divisória a desenhar.
  getUnreadMarker: $state => chatId =>
    $state.unreadMarkerByChat[chatId] || null,

  getChats($state) {
    return safeChats($state);
  },
  getChannels($state) {
    return safeChats($state).filter(c => c.chat_type === 'channel');
  },
  getGroups($state) {
    return safeChats($state).filter(c => c.chat_type === 'group');
  },
  getDms($state) {
    return safeChats($state).filter(c => c.chat_type === 'direct_message');
  },
  getActiveChat($state) {
    return $state.activeChat;
  },
  getMessages($state) {
    return chatId => $state.messages[chatId] || [];
  },
  getAgentsCache($state) {
    return $state.agentsCache;
  },
  getUnreadByChat($state) {
    return $state.unreadByChat;
  },
  totalUnread($state) {
    return Object.values($state.unreadByChat).reduce(
      (sum, count) => sum + (count || 0),
      0
    );
  },
  // A lista lê a falha por aqui, e não pelo `uiFlags` inteiro: é o getter de
  // erro do módulo que diz "quem trata é o store" (ver `falha-silenciosa` em
  // scripts/verifica-padrao.mjs).
  getFetchChatsError($state) {
    return $state.uiFlags.fetchChatsError;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

const actions = {
  async list({ commit }) {
    commit('SET_UI_FLAG', { isFetchingChats: true });
    try {
      const response = await InternalChatsAPI.list();
      commit('SET_CHATS', response.data);
      commit('SET_UI_FLAG', { fetchChatsError: false });
    } catch (error) {
      // Era "Ignore silently — UI stays empty": a lista vazia dizia "nenhuma
      // conversa" para quem tinha vinte. A falha vira bandeira, e a lista
      // (`ChatList.vue`) mostra o estado de erro com "tentar de novo".
      commit('SET_UI_FLAG', { fetchChatsError: true });
    } finally {
      commit('SET_UI_FLAG', { isFetchingChats: false });
    }
  },

  async get({ commit }, id) {
    const response = await InternalChatsAPI.show(id);
    commit('UPDATE_CHAT', response.data);
    return response.data;
  },

  async createDm({ commit }, userId) {
    commit('SET_UI_FLAG', { isCreatingDm: true });
    try {
      const response = await InternalChatsAPI.createDm(userId);
      commit('ADD_CHAT', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreatingDm: false });
    }
  },

  async createGroup({ commit }, { name, description, memberIds, avatar }) {
    commit('SET_UI_FLAG', { isCreatingGroup: true });
    try {
      const response = await InternalChatsAPI.createGroup({
        name,
        description,
        memberIds,
        avatar,
      });
      commit('ADD_CHAT', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreatingGroup: false });
    }
  },

  // Item 16 da fila: canal com permissão por departamento.
  async createChannel({ commit }, { name, description, teamId }) {
    commit('SET_UI_FLAG', { isCreatingChannel: true });
    try {
      const response = await InternalChatsAPI.createChannel({
        name,
        description,
        teamId,
      });
      commit('ADD_CHAT', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreatingChannel: false });
    }
  },

  async update({ commit }, { id, ...payload }) {
    const response = await InternalChatsAPI.update(id, payload);
    commit('UPDATE_CHAT', response.data);
    return response.data;
  },

  async destroy({ commit }, id) {
    await InternalChatsAPI.destroy(id);
    commit('REMOVE_CHAT', id);
  },

  async addMembers({ commit }, { id, userIds }) {
    const response = await InternalChatsAPI.addMembers(id, userIds);
    commit('UPDATE_CHAT', response.data);
    return response.data;
  },

  async removeMember(
    { commit, rootGetters, state: chatsState },
    { id, userId }
  ) {
    const response = await InternalChatsAPI.removeMember(id, userId);
    // Backend sempre responde 204 (sem corpo), removendo membro ou eu mesmo
    // saindo — não dá pra distinguir pelo response. Comparamos com o
    // usuário atual: se sou eu, perdi acesso ao grupo (some da lista). Se é
    // outro membro, o grupo continua existindo pra mim — só atualiza a
    // lista de participantes local (achado 2026-07-15: antes SEMPRE
    // removia o chat inteiro da lista, mesmo removendo outra pessoa).
    const currentUser = rootGetters.getCurrentUser;
    if (currentUser && currentUser.id === userId) {
      commit('REMOVE_CHAT', id);
    } else {
      const chat = chatsState.chats.find(c => c.id === id);
      if (chat) {
        const users = (chat.users || []).filter(u => u.id !== userId);
        commit('UPDATE_CHAT', { ...chat, users, member_count: users.length });
      }
    }
    return response.data;
  },

  // Devolve ONDE O USUARIO PAROU (o `last_read_at` de antes desta chamada), para
  // a divisoria "novas mensagens". Quem chama decide congelar: reconsultar
  // depois traria o instante de agora e a divisoria sumiria.
  async markRead({ commit, state: s }, id) {
    try {
      const { data } = await InternalChatsAPI.markRead(id);
      commit('RESET_UNREAD', id);

      // Só a primeira chamada por conversa carrega a marca verdadeira; da
      // segunda em diante o servidor devolve o instante de agora. Sem esta
      // guarda a divisória sumiria conforme a ordem de montagem.
      if (!(id in s.unreadMarkerByChat)) {
        commit('SET_UNREAD_MARKER', {
          chatId: id,
          marker: data?.previous_last_read_at || null,
        });
      }
      return s.unreadMarkerByChat[id] || null;
    } catch (error) {
      return null;
    }
  },

  // Some com a divisória — usado ao sair da conversa, para ela não reaparecer
  // na próxima visita.
  clearUnreadMarker({ commit }, id) {
    commit('SET_UNREAD_MARKER', { chatId: id, marker: null });
  },

  async fetchMessages({ commit }, { chatId, offset = 0, limit = 50 }) {
    // [polish] only set isFetchingMessages for initial load (offset=0),
    // not for pagination loads which are tracked by isLoadingOlder in the component
    const isInitialLoad = offset === 0;
    if (isInitialLoad) commit('SET_UI_FLAG', { isFetchingMessages: true });
    try {
      const response = await InternalChatsAPI.listMessages(chatId, {
        offset,
        limit,
      });
      const messages = response.data?.data || response.data || [];
      if (isInitialLoad) {
        commit('SET_MESSAGES', { chatId, messages });
      } else {
        // Prepend older messages without clobbering newer ones
        commit('PREPEND_MESSAGES', { chatId, messages });
      }
      return messages;
    } finally {
      if (isInitialLoad) commit('SET_UI_FLAG', { isFetchingMessages: false });
    }
  },

  // Envio otimista: mensagem de texto puro aparece na hora (estado "pendente"),
  // sem esperar o round-trip do servidor — trava a experiência em rede lenta,
  // achado como bug real (CHAT-INTERNO-EVOLUCAO-FILA.md item 3). Anexo fica
  // de fora do caminho otimista (precisa da URL real pós-upload pra preview
  // — escopo maior, não vale a complexidade agora). `retryMessage` reusa o
  // mesmo tempId de uma mensagem que falhou antes, em vez de duplicar.
  async sendMessage(
    { commit, rootGetters },
    {
      chatId,
      content,
      attachments = [],
      inReplyToId = null,
      retryMessage = null,
      scheduledAt = null,
    }
  ) {
    const isRetry = !!retryMessage;
    // Programada NUNCA entra otimista, e nem aparece quando o POST volta: ela
    // não existe na conversa até a hora chegar. Sem esta guarda, quem escreveu
    // veria a própria mensagem na coluna na mesma hora — que é exatamente o que
    // programar existe para evitar.
    const isOptimistic = !scheduledAt && (isRetry || attachments.length === 0);
    const tempId = isRetry
      ? retryMessage.id
      : `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    if (isRetry) {
      commit('UPDATE_MESSAGE', {
        chatId,
        message: { ...retryMessage, localPending: true, localFailed: false },
      });
    } else if (isOptimistic) {
      const currentUser = rootGetters.getCurrentUser;
      commit('ADD_MESSAGE', {
        chatId,
        message: {
          id: tempId,
          // Sem isso, MessageBubble não sabe o chatId de uma mensagem
          // pendente/falha — botão de retry mandava pra
          // `/internal_chats/undefined/messages` (404, bug real achado).
          chat_id: chatId,
          content,
          sender: currentUser
            ? {
                id: currentUser.id,
                name: currentUser.name,
                avatar_url: currentUser.avatar_url,
              }
            : null,
          created_at: new Date().toISOString(),
          attachments: [],
          in_reply_to: null,
          localPending: true,
        },
      });
    }

    commit('SET_UI_FLAG', { isSendingMessage: true });

    // Serializa o POST de verdade por chat (ver comentário na declaração de
    // sendQueues acima) — a inserção otimista acima continua instantânea,
    // só o request de rede é que espera o anterior terminar.
    const previousInQueue = sendQueues[chatId] || Promise.resolve();
    const queuedRequest = previousInQueue
      .catch(() => {})
      .then(() =>
        InternalChatsAPI.createMessage(chatId, {
          content,
          attachments,
          inReplyToId,
          scheduledAt,
        })
      );
    sendQueues[chatId] = queuedRequest;

    try {
      const response = await queuedRequest;
      if (isOptimistic) {
        commit('REPLACE_MESSAGE', { chatId, tempId, message: response.data });
      } else if (!scheduledAt) {
        commit('ADD_MESSAGE', { chatId, message: response.data });
      }
      return response.data;
    } catch (error) {
      if (isOptimistic) {
        commit('MARK_MESSAGE_FAILED', { chatId, tempId });
      }
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isSendingMessage: false });
    }
  },

  async updateMessage({ commit }, { chatId, messageId, content }) {
    const response = await InternalChatsAPI.updateMessage(
      chatId,
      messageId,
      content
    );
    commit('UPDATE_MESSAGE', { chatId, message: response.data });
    return response.data;
  },

  async deleteMessage({ commit }, { chatId, messageId }) {
    await InternalChatsAPI.deleteMessage(chatId, messageId);
    commit('REMOVE_MESSAGE', { chatId, messageId });
  },

  // Otimista: aplica localmente antes da resposta do servidor (toggle é
  // rápido e de baixo risco — reverte com REPLACE se a API falhar). O
  // broadcast websocket 'reaction.updated' cobre os outros participantes.
  async toggleReaction({ commit, rootGetters }, { chatId, messageId, emoji }) {
    const currentUserId = rootGetters.getCurrentUserID;
    const messages = state.messages[chatId] || [];
    const message = messages.find(m => m.id === messageId);
    const previousReactions = message?.reactions || [];
    const alreadyReacted = previousReactions.some(
      r => r.emoji === emoji && r.user_id === currentUserId
    );
    const optimisticReactions = alreadyReacted
      ? previousReactions.filter(
          r => !(r.emoji === emoji && r.user_id === currentUserId)
        )
      : [...previousReactions, { emoji, user_id: currentUserId }];

    commit('SET_MESSAGE_REACTIONS', {
      chatId,
      messageId,
      reactions: optimisticReactions,
    });
    try {
      await InternalChatsAPI.toggleReaction(chatId, messageId, emoji);
    } catch (error) {
      // Reverte pro estado anterior — o broadcast (se a call teve sucesso no
      // servidor mas a resposta se perdeu) corrige de qualquer forma depois.
      commit('SET_MESSAGE_REACTIONS', {
        chatId,
        messageId,
        reactions: previousReactions,
      });
      throw error;
    }
  },

  // Otimista igual toggleReaction: aplica local antes da resposta, reverte
  // se falhar. Broadcast 'message.pinned' cobre os outros participantes.
  async togglePin({ commit, rootGetters }, { chatId, messageId }) {
    const currentUserId = rootGetters.getCurrentUserID;
    const messages = state.messages[chatId] || [];
    const message = messages.find(m => m.id === messageId);
    const wasPinned = !!message?.pinned_at;
    const optimisticChanges = wasPinned
      ? { pinned_at: null, pinned_by_id: null }
      : { pinned_at: new Date().toISOString(), pinned_by_id: currentUserId };

    commit('PATCH_MESSAGE', { chatId, messageId, changes: optimisticChanges });
    try {
      await InternalChatsAPI.togglePin(chatId, messageId);
    } catch (error) {
      commit('PATCH_MESSAGE', {
        chatId,
        messageId,
        changes: wasPinned
          ? { pinned_at: message.pinned_at, pinned_by_id: message.pinned_by_id }
          : { pinned_at: null, pinned_by_id: null },
      });
      throw error;
    }
  },

  // Item 9 da fila: silenciar conversa. Otimista (revert em erro), igual
  // togglePin/toggleReaction já fazem.
  async toggleMute({ commit }, { chatId, duration }) {
    const chat =
      safeChats(state).find(c => c.id === chatId) || state.activeChat;
    const previous = chat
      ? { muted: chat.muted, muted_until: chat.muted_until }
      : null;
    const optimisticMuted = duration !== 'off';
    if (chat) {
      commit('UPDATE_CHAT', { ...chat, muted: optimisticMuted });
    }
    try {
      const response = await InternalChatsAPI.toggleMute(chatId, duration);
      if (chat) {
        commit('UPDATE_CHAT', {
          ...chat,
          muted_until: response.data.muted_until,
          muted: !!response.data.muted_until,
        });
      }
    } catch (error) {
      if (chat && previous) {
        commit('UPDATE_CHAT', { ...chat, ...previous });
      }
      throw error;
    }
  },

  async fetchAgents({ commit }, query = '') {
    const response = await InternalChatsAPI.agents(query);
    commit('SET_AGENTS', response.data);
    return response.data;
  },

  setActiveChat({ commit }, chat) {
    commit('SET_ACTIVE_CHAT', chat);
  },
};

const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_CHATS($state, chats) {
    // 204/503 podem mandar corpo vazio ("") → nunca deixar virar não-array.
    $state.chats = Array.isArray(chats) ? chats : [];
    // Seed unread counts from API payload
    $state.chats.forEach(chat => {
      if (
        chat.unread_count !== undefined &&
        $state.unreadByChat[chat.id] === undefined
      ) {
        $state.unreadByChat = {
          ...$state.unreadByChat,
          [chat.id]: chat.unread_count || 0,
        };
      }
    });
  },

  ADD_CHAT($state, chat) {
    const exists = $state.chats.some(c => c.id === chat.id);
    if (!exists) {
      $state.chats = [...$state.chats, chat];
    }
  },

  UPDATE_CHAT($state, updatedChat) {
    const index = $state.chats.findIndex(c => c.id === updatedChat.id);
    if (index !== -1) {
      const chats = [...$state.chats];
      chats.splice(index, 1, updatedChat);
      $state.chats = chats;
    } else {
      $state.chats = [...$state.chats, updatedChat];
    }
    if ($state.activeChat && $state.activeChat.id === updatedChat.id) {
      $state.activeChat = updatedChat;
    }
  },

  // Pedido do Victor: status enviada/entregue/vista em tempo real. Merge
  // parcial (só other_user.last_read_at) — UPDATE_CHAT substitui o chat
  // inteiro, perderia outros campos se usado aqui.
  SET_OTHER_USER_LAST_READ($state, { chatId, lastReadAt }) {
    const patch = chat =>
      chat.other_user
        ? {
            ...chat,
            other_user: { ...chat.other_user, last_read_at: lastReadAt },
          }
        : chat;

    $state.chats = $state.chats.map(c => (c.id === chatId ? patch(c) : c));
    if ($state.activeChat && $state.activeChat.id === chatId) {
      $state.activeChat = patch($state.activeChat);
    }
  },

  REMOVE_CHAT($state, id) {
    $state.chats = $state.chats.filter(c => c.id !== id);
    if ($state.activeChat && $state.activeChat.id === id) {
      $state.activeChat = null;
    }
    const unread = { ...$state.unreadByChat };
    delete unread[id];
    $state.unreadByChat = unread;
  },

  SET_ACTIVE_CHAT($state, chat) {
    $state.activeChat = chat;
  },

  SET_MESSAGES($state, { chatId, messages }) {
    $state.messages = { ...$state.messages, [chatId]: messages };
  },

  // [polish] prepend older messages (pagination) without clobbering newer ones
  PREPEND_MESSAGES($state, { chatId, messages }) {
    const existing = $state.messages[chatId] || [];
    const existingIds = new Set(existing.map(m => m.id));
    const newOnes = messages.filter(m => !existingIds.has(m.id));
    $state.messages = {
      ...$state.messages,
      [chatId]: [...newOnes, ...existing],
    };
  },

  ADD_MESSAGE($state, { chatId, message }) {
    const existing = $state.messages[chatId] || [];
    const alreadyExists = existing.some(m => m.id === message.id);
    if (!alreadyExists) {
      $state.messages = {
        ...$state.messages,
        [chatId]: [...existing, message],
      };
    }
  },

  UPDATE_MESSAGE($state, { chatId, message }) {
    const existing = $state.messages[chatId] || [];
    const idx = existing.findIndex(m => m.id === message.id);
    if (idx === -1) return;
    const updated = [...existing];
    updated.splice(idx, 1, message);
    $state.messages = { ...$state.messages, [chatId]: updated };
  },

  // Troca a mensagem otimista (tempId) pela real vinda do servidor. Se o
  // tempId já não existir (ex: broadcast websocket chegou primeiro e o
  // ADD_MESSAGE dele já fez dedup por id real), cai no fallback de
  // dedup-add normal em vez de não fazer nada.
  REPLACE_MESSAGE($state, { chatId, tempId, message }) {
    const existing = $state.messages[chatId] || [];
    const tempIdx = existing.findIndex(m => m.id === tempId);
    const realAlreadyPresent = existing.some(m => m.id === message.id);

    // O broadcast websocket (message.created) pode chegar ANTES desse
    // REPLACE resolver — se já adicionou a mensagem real via ADD_MESSAGE,
    // só precisamos remover o placeholder temporário, não inserir de novo
    // (bug real achado: duplicava a bolha até dar reload).
    if (realAlreadyPresent) {
      $state.messages = {
        ...$state.messages,
        [chatId]: existing.filter(m => m.id !== tempId),
      };
      return;
    }
    if (tempIdx === -1) {
      $state.messages = {
        ...$state.messages,
        [chatId]: [...existing, message],
      };
      return;
    }
    const updated = [...existing];
    updated.splice(tempIdx, 1, message);
    $state.messages = { ...$state.messages, [chatId]: updated };
  },

  MARK_MESSAGE_FAILED($state, { chatId, tempId }) {
    const existing = $state.messages[chatId] || [];
    const idx = existing.findIndex(m => m.id === tempId);
    if (idx === -1) return;
    const updated = [...existing];
    updated.splice(idx, 1, {
      ...updated[idx],
      localPending: false,
      localFailed: true,
    });
    $state.messages = { ...$state.messages, [chatId]: updated };
  },

  REMOVE_MESSAGE($state, { chatId, messageId }) {
    const existing = $state.messages[chatId] || [];
    $state.messages = {
      ...$state.messages,
      [chatId]: existing.filter(m => m.id !== messageId),
    };
  },

  // Merge parcial (não substitui a mensagem inteira) — usado pelo broadcast
  // websocket de edição, que só manda os campos que mudaram, pra não perder
  // sender/attachments/reactions/in_reply_to já carregados localmente.
  PATCH_MESSAGE($state, { chatId, messageId, changes }) {
    const existing = $state.messages[chatId] || [];
    const idx = existing.findIndex(m => m.id === messageId);
    if (idx === -1) return;
    const updated = [...existing];
    updated.splice(idx, 1, { ...updated[idx], ...changes });
    $state.messages = { ...$state.messages, [chatId]: updated };
  },

  SET_MESSAGE_REACTIONS($state, { chatId, messageId, reactions }) {
    const existing = $state.messages[chatId] || [];
    const idx = existing.findIndex(m => m.id === messageId);
    if (idx === -1) return;
    const updated = [...existing];
    updated.splice(idx, 1, { ...updated[idx], reactions });
    $state.messages = { ...$state.messages, [chatId]: updated };
  },

  // A prévia da lista: a última mensagem da conversa e a hora dela. Chamada
  // pelo cabo quando chega mensagem — sem isto a lista mostrava a última do
  // carregamento até alguém recarregar a página.
  SET_LAST_MESSAGE($state, { chatId, lastMessage }) {
    const aplicar = chat =>
      chat && chat.id === chatId
        ? {
            ...chat,
            last_message: lastMessage,
            last_message_at: lastMessage.created_at,
          }
        : chat;
    $state.chats = $state.chats.map(aplicar);
    $state.activeChat = aplicar($state.activeChat);
  },

  INCREMENT_UNREAD($state, chatId) {
    $state.unreadByChat = {
      ...$state.unreadByChat,
      [chatId]: ($state.unreadByChat[chatId] || 0) + 1,
    };
  },

  SET_UNREAD_MARKER($state, { chatId, marker }) {
    $state.unreadMarkerByChat = {
      ...$state.unreadMarkerByChat,
      [chatId]: marker,
    };
  },

  RESET_UNREAD($state, chatId) {
    $state.unreadByChat = { ...$state.unreadByChat, [chatId]: 0 };
  },

  SET_AGENTS($state, agents) {
    $state.agentsCache = agents;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
